/* ==========================================================================
   st-ab-player.js — A/B player engine for sections/st-ab-player.liquid
   --------------------------------------------------------------------------
   Loaded ONLY by that section. Web Audio API, never <audio>: an <audio> element
   cannot switch sources without a gap.

   How the gapless switch works
   ---------------------------
   Both files for the selected sample are decoded once, then BOTH play at the
   same time, forever, on one clock:

       dryBuffer -> drySrc -> dryGain -\
                                        >-- destination
       wetBuffer -> wetSrc -> wetGain -/

   Both sources are started with the SAME scheduled `when` value, so they can
   never drift relative to each other no matter how long the loop runs. The
   OFF/ON toggle does nothing except ramp the two gains past each other over
   XFADE seconds. Nothing restarts, nothing reloads, so switching is instant and
   click-free at any point in the loop.

   Setting a gain instantly would click, so every change is a linear ramp.

   Guard: the two bounces must be the same length to the sample. If they differ
   by more than MAX_SKEW they will slowly drift apart and the plugin will appear
   to do things it does not, which is near-impossible to diagnose by ear. So we
   compare decoded durations and refuse to play instead.
   ========================================================================== */
(function () {
  'use strict';

  var XFADE    = 0.015;   // 15ms audio crossfade
  var SHAPE_XFADE = 150;  // ms, the waveform picture crossfades with the sound
  var BINS_WIDE = 400;    // desktop bins; mobile draws 200 by pairing these
  var MOBILE_MAX = 699;   // below this the display halves to 200 bins
  var LOOKAHEAD= 0.08;    // schedule the shared start slightly ahead of now
  var MAX_SKEW = 0.010;   // 10ms: past this the two files drift audibly
  var REST_ALPHA = 0.5;   // shape brightness with nothing playing (no playhead
                          // on screen, so the played/unplayed split means nothing)

  var AC = window.AudioContext || window.webkitAudioContext;

  /* Peak envelope: reduce a buffer to `bins` values, each the absolute peak of
     every channel across that slice. Computed ONCE per file on decode and
     cached; never per frame. Mobile pairs adjacent bins at draw time, and
     max(max(a),max(b)) === the peak of the merged slice, so 400 paired down to
     200 is identical to computing 200 directly. */
  function envelopeOf(buffer, bins) {
    var chans = buffer.numberOfChannels, len = buffer.length;
    var data = [], c, i, b;
    for (c = 0; c < chans; c++) data.push(buffer.getChannelData(c));
    var out = new Float32Array(bins);
    var step = len / bins;
    for (b = 0; b < bins; b++) {
      var from = Math.floor(b * step);
      var to = b === bins - 1 ? len : Math.floor((b + 1) * step);
      var peak = 0;
      for (c = 0; c < chans; c++) {
        var d = data[c];
        for (i = from; i < to; i++) { var v = d[i]; if (v < 0) v = -v; if (v > peak) peak = v; }
      }
      out[b] = peak;
    }
    return out;
  }

  function peakOf(env) {
    var m = 0;
    for (var i = 0; i < env.length; i++) if (env[i] > m) m = env[i];
    return m;
  }

  /* Stored envelope: the SAME BINS_WIDE-bin peak envelope as envelopeOf(), but
     computed offline and carried on the cell as base64, so the shape can be on
     screen before a byte of audio is fetched. 400 bytes -> 536 base64 chars per
     file. Both files were quantised with ONE shared factor, so 255 means the
     louder file's peak for BOTH of them; that is the shared-scale rule baked
     into the data where nothing downstream can undo it.
     Anything that is not exactly BINS_WIDE bytes is refused: a wrong-length
     string would draw a shape that is not the audio, which is worse than no
     shape at all. */
  function decodeEnv(b64) {
    if (!b64) return null;
    var bin;
    try { bin = atob(b64); } catch (e) { return null; }
    if (bin.length !== BINS_WIDE) return null;
    var out = new Uint8Array(BINS_WIDE);
    for (var i = 0; i < BINS_WIDE; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  function decode(ctx, arrayBuffer) {
    // Safari's older callback form has no promise; support both.
    return new Promise(function (resolve, reject) {
      var p;
      try { p = ctx.decodeAudioData(arrayBuffer, resolve, reject); }
      catch (e) { reject(e); return; }
      if (p && typeof p.then === 'function') p.then(resolve, reject);
    });
  }

  function setup(root) {
    if (root.__stAbInit) return;
    root.__stAbInit = true;

    var player = root.querySelector('[data-st-ab-player]');
    // Videos now live in their own section (st-video-demos) and reuse the
    // delegated [data-st-video] handler in streaky.js.
    if (!player) return;
    // No Web Audio at all: leave the player hidden rather than show a dead control.
    if (!AC) { console.warn('[st-ab-player] Web Audio unavailable, player hidden.'); return; }

    var cells   = [].slice.call(player.querySelectorAll('[data-st-ab-sample]'));
    var toggle  = player.querySelector('[data-st-ab-toggle]');
    var offLbl  = player.querySelector('[data-st-ab-off]');
    var onLbl   = player.querySelector('[data-st-ab-on]');
    // status lives OUTSIDE the player wrapper so it survives the player being hidden
    var statusEl= root.querySelector('[data-st-ab-status]');
    if (!cells.length || !toggle) return;

    player.hidden = false;
    toggle.disabled = false;

    var ctx = null;
    var selected = 0;
    var on = false;            // false = dry (OFF), true = wet (ON)
    var playing = false;
    var loading = false;
    var buffers = null;        // { dry, wet } for the SELECTED sample only
    var loadedFor = -1;
    var nodes = null;          // { drySrc, wetSrc, dryGain, wetGain }
    var waveform = null;       // { dry, wet, scale } whatever is on screen right now
    /* The live envelope for whatever `buffers` holds. It has to live beside the
       buffers, not in `waveform`, because `waveform` also carries the STORED
       shape at rest and gets swapped around by restShape(). Cleared in exactly
       the two places `buffers` is cleared, so the two can never disagree. */
    var liveWave = null;
    var startedAt = 0;         // ctx time both sources were scheduled at
    var clipDur = 0;
    var shapeMix = 0;          // 0 = BEFORE shape, 1 = AFTER shape
    var shapeFrom = 0, shapeAt = 0;
    var rafId = null;
    var broken = {};           // sample index -> reason
    var token = 0;             // guards against a slow load landing after a switch

    function say(msg, isError) {
      if (!statusEl) return;
      statusEl.textContent = msg || '';
      statusEl.classList.toggle('is-error', !!isError);
    }
    function labelOf(i) {
      var b = cells[i] && cells[i].querySelector('b');
      return b ? b.textContent.trim() : 'sample ' + (i + 1);
    }

    function paint() {
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (offLbl) offLbl.classList.toggle('is-on', !on);
      if (onLbl)  onLbl.classList.toggle('is-on', on);
      cells.forEach(function (c, i) {
        c.setAttribute('aria-current', i === selected ? 'true' : 'false');
        c.classList.toggle('is-playing', i === selected && playing);
        c.classList.toggle('is-loading', i === selected && loading);
      });
    }

    function ensureCtx() {
      // Must happen inside the user gesture or mobile Safari never starts.
      if (!ctx) ctx = new AC();
      if (ctx.state === 'suspended' && ctx.resume) ctx.resume();
      return ctx;
    }

    function stopSources() {
      if (nodes) {
        ['drySrc', 'wetSrc'].forEach(function (k) {
          var s = nodes[k];
          if (!s) return;
          try { s.stop(); } catch (e) {}
          try { s.disconnect(); } catch (e) {}
        });
        ['dryGain', 'wetGain'].forEach(function (k) {
          try { nodes[k].disconnect(); } catch (e) {}
        });
        nodes = null;
      }
      playing = false;
      stopLoop();
      // The sample is still loaded, so the shape stays on screen with the
      // playhead simply stopped. Only release() drops the buffers and hides it.
      if (canvas && waveform) drawWave();
    }

    function applyGains(when, ramp) {
      if (!nodes) return;
      var dryTarget = on ? 0 : 1;
      var wetTarget = on ? 1 : 0;
      [[nodes.dryGain, dryTarget], [nodes.wetGain, wetTarget]].forEach(function (pair) {
        var g = pair[0].gain, target = pair[1];
        if (ramp) {
          // Read the live value first, so a toggle mid-ramp continues smoothly
          // instead of jumping. Never assign .value directly: that clicks.
          var current = g.value;
          g.cancelScheduledValues(when);
          g.setValueAtTime(current, when);
          g.linearRampToValueAtTime(target, when + XFADE);
        } else {
          g.setValueAtTime(target, when);
        }
      });
    }

    function startSources() {
      if (!buffers) return;
      stopSources();
      /* The picture must be of the audio we are about to play. The cached-replay
         path in playSample() gets here without going through load(), and by then
         restShape() may have put a DIFFERENT sample's stored shape in `waveform`:
         click A, click B (starts fetching, shows B's stored shape), click A again
         before B lands -> A plays under B's waveform. Re-assert it here. */
      if (liveWave) waveform = liveWave;
      var drySrc = ctx.createBufferSource();
      var wetSrc = ctx.createBufferSource();
      drySrc.buffer = buffers.dry; wetSrc.buffer = buffers.wet;
      drySrc.loop = true;         wetSrc.loop = true;
      var dryGain = ctx.createGain(), wetGain = ctx.createGain();
      drySrc.connect(dryGain).connect(ctx.destination);
      wetSrc.connect(wetGain).connect(ctx.destination);
      nodes = { drySrc: drySrc, wetSrc: wetSrc, dryGain: dryGain, wetGain: wetGain };

      // ONE shared timestamp for both starts. This is what locks the playheads.
      var when = ctx.currentTime + LOOKAHEAD;
      applyGains(when, false);
      drySrc.start(when);
      wetSrc.start(when);
      startedAt = when;
      clipDur = buffers.dry.duration;
      playing = true;
      shapeMix = on ? 1 : 0; shapeAt = 0;
      // must be visible BEFORE measuring: a [hidden] canvas has clientWidth 0
      if (canvas) canvas.hidden = false;
      sizeCanvas();
      drawWave();          // paint one frame immediately, before any rAF
      startLoop();
      paint();
      say('Playing ' + labelOf(selected) + '. Plugin ' + (on ? 'on' : 'off') + '.');
    }

    function load(i) {
      var my = ++token;
      loading = true; paint();
      say('Loading ' + labelOf(i) + '…');
      var cell = cells[i];
      var urls = [cell.getAttribute('data-dry'), cell.getAttribute('data-wet')];
      return Promise.all(urls.map(function (u) {
        return fetch(u, { mode: 'cors' }).then(function (r) {
          if (!r.ok) { var he = new Error('HTTP ' + r.status + ' for ' + u); he.permanent = true; throw he; }
          return r.arrayBuffer();
        }).then(function (ab) { return decode(ctx, ab); });
      })).then(function (bufs) {
        if (my !== token) return null;                 // superseded by a newer click
        var skew = Math.abs(bufs[0].duration - bufs[1].duration);
        if (skew > MAX_SKEW) {
          var se = new Error('dry and wet differ by ' + (skew * 1000).toFixed(1) +
            'ms (limit ' + (MAX_SKEW * 1000) + 'ms). They will drift apart while looping. ' +
            'Re-bounce the same bar range twice and do not trim either file.');
          se.permanent = true;                         // a bad bounce never fixes itself
          throw se;
        }
        buffers = { dry: bufs[0], wet: bufs[1] };
        /* THE RULE THAT MAKES THIS HONEST: one scale factor for both shapes,
           taken from the louder of the two peaks. Normalising each independently
           would make them look identical and the comparison would be worthless.
           Because the files are level matched, AFTER genuinely reads as more
           even with lower peaks — that difference is measured, not drawn. */
        var envDry = envelopeOf(bufs[0], BINS_WIDE);
        var envWet = envelopeOf(bufs[1], BINS_WIDE);
        var shared = Math.max(peakOf(envDry), peakOf(envWet)) || 1;
        liveWave = { dry: envDry, wet: envWet, scale: 1 / shared,
                     peakDry: peakOf(envDry), peakWet: peakOf(envWet), sharedPeak: shared };
        waveform = liveWave;
        loadedFor = i;
        loading = false; paint();
        return buffers;
      }).catch(function (err) {
        if (my !== token) return null;
        loading = false;
        buffers = null; liveWave = null; loadedFor = -1;
        console.error('[st-ab-player] "' + labelOf(i) + '" cannot play: ' + err.message);
        /* A bad bounce or a 404 will never fix itself, so lock that sample off.
           A dropped connection might, so leave the cell tappable to retry
           instead of permanently killing it (and possibly the whole player)
           because the visitor went through a tunnel. */
        if (err.permanent) {
          broken[i] = err.message;
          cells[i].disabled = true;
          say('This sample is unavailable.', true);
        } else {
          say('Could not load that sample. Tap to try again.', true);
        }
        paint();
        if (cells.every(function (c, ci) { return broken[ci]; })) {
          player.hidden = true;
          console.warn('[st-ab-player] no playable samples, player hidden.');
        }
        return null;
      });
    }

    /* ---- waveform display ------------------------------------------------
       Drawn from the cached envelopes. Nothing is recomputed here: draw() only
       reads Float32Arrays and interpolates between the two cached shapes. */
    var canvas = root.querySelector('[data-st-ab-wave]');
    var cctx = canvas ? canvas.getContext('2d') : null;

    /* Tokens are declared on .st (streaky-system.css), and `root` IS the .st
       element here, so read them from it — never documentElement, where they
       are not defined. Canvas needs real colour values, not var() strings. */
    /* NB: named cssToken, not token — `var token` is already the load-generation
       counter in this same scope, and a `function token` declaration would be
       overwritten by it, throwing here and aborting setup before any handler
       is attached. */
    function cssToken(name, fallback) {
      var v = getComputedStyle(root).getPropertyValue(name).trim();
      return v || fallback;
    }
    var ACCENT = cssToken('--st-accent', '#0071e3');
    var TEXT   = cssToken('--st-text',   '#f5f5f7');

    function reducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }
    function binsForWidth(w) { return w <= MOBILE_MAX ? BINS_WIDE / 2 : BINS_WIDE; }

    var sizedW = 0, sizedH = 0;   // what sizeCanvas() last actually applied
    function sizeCanvas() {
      if (!canvas || !cctx) return 0;
      var cssW = canvas.clientWidth;
      if (cssW < 2) return 0;
      var cssH = cssW <= MOBILE_MAX ? 96 : 132;
      var dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
      sizedW = cssW; sizedH = cssH;
      canvas.style.height = cssH + 'px';
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      cctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return cssW;
    }

    function playhead() {
      if (!playing || !ctx || !clipDur) return 0;
      var t = ctx.currentTime - startedAt;
      if (t < 0) return 0;
      return (t % clipDur) / clipDur;
    }

    function drawWave() {
      if (!canvas || !cctx || !waveform) return;
      if (canvas.hidden) canvas.hidden = false;
      if (!canvas.style.height) sizeCanvas();
      var cssW = canvas.clientWidth;
      if (cssW < 2) return;
      var cssH = parseFloat(canvas.style.height) || 132;
      var bins = binsForWidth(cssW);
      var pair = BINS_WIDE / bins;                 // 1 on desktop, 2 on mobile
      var mid = cssH / 2, maxA = mid - 2;
      var gap = cssW / bins, bw = Math.max(1, gap * 0.62);
      var head = playhead();

      cctx.clearRect(0, 0, cssW, cssH);
      for (var b = 0; b < bins; b++) {
        var d = 0, w = 0, k, idx;
        for (k = 0; k < pair; k++) {               // pairing preserves the true peak
          idx = b * pair + k;
          if (waveform.dry[idx] > d) d = waveform.dry[idx];
          if (waveform.wet[idx] > w) w = waveform.wet[idx];
        }
        // ONE shared scale, applied to both shapes
        var v = (d * (1 - shapeMix) + w * shapeMix) * waveform.scale;
        var a = Math.max(1, v * maxA);
        var x = b * gap;
        cctx.fillStyle = ACCENT;
        cctx.globalAlpha = playing ? ((b / bins) <= head ? 0.95 : 0.28) : REST_ALPHA;
        cctx.fillRect(x, mid - a, bw, a * 2);
      }
      cctx.globalAlpha = 1;
      if (playing) {
        var px = head * cssW;
        cctx.fillStyle = TEXT; cctx.globalAlpha = 0.85;
        cctx.fillRect(px, 0, 1.5, cssH);
        cctx.globalAlpha = 1;
      }
    }

    /* The at-rest picture. Reads the pre-computed envelope off the selected
       cell; fetches and decodes nothing. Returns to this whenever there is no
       live audio (first paint, stop, scroll-out), and to a blank hidden canvas
       for any sample that has no stored envelope. */
    function storedWaveOf(i) {
      var cell = cells[i];
      if (!cell) return null;
      var d = decodeEnv(cell.getAttribute('data-env-dry'));
      var w = decodeEnv(cell.getAttribute('data-env-wet'));
      if (!d || !w) return null;
      // 1/255 IS the shared scale: it is already baked into both arrays.
      return { dry: d, wet: w, scale: 1 / 255, stored: true };
    }

    function restShape() {
      if (playing) return;                       // live audio owns the canvas
      waveform = storedWaveOf(selected);
      if (!waveform) { if (canvas) canvas.hidden = true; return; }
      if (!canvas || !cctx) return;
      canvas.hidden = false;                     // must be visible before measuring
      if (sizeCanvas()) drawWave();
      // measured zero? syncCanvas() draws it the moment the box is real.
    }

    /* Watch the box rather than guessing when it settles. Two real cases this
       covers that a one-shot retry does not:
         - a tab opened in the BACKGROUND lays out nothing until it is looked at,
           and requestAnimationFrame does not run there at all, so a rAF retry
           never fires and the canvas stays 300x150 and blank;
         - a late webfont or stylesheet moves the container after first paint.
       sizeCanvas() clears the bitmap, so only re-size when the box really moved. */
    function syncCanvas() {
      if (!canvas || !cctx || canvas.hidden) return;
      var w = canvas.clientWidth;
      if (w < 2) return;
      var h = w <= MOBILE_MAX ? 96 : 132;
      if (w === sizedW && h === sizedH) return;
      sizeCanvas();
      if (waveform) drawWave();
    }

    function tickShape(now) {
      if (!shapeAt) return;
      var k = Math.min(1, (now - shapeAt) / SHAPE_XFADE);
      var target = on ? 1 : 0;
      shapeMix = shapeFrom + (target - shapeFrom) * k;
      if (k >= 1) shapeAt = 0;
    }

    function frame(now) {
      if (!playing) { rafId = null; return; }
      tickShape(now);
      drawWave();
      rafId = requestAnimationFrame(frame);
    }
    function startLoop() {
      if (rafId != null || !waveform) return;
      if (canvas) canvas.hidden = false;
      rafId = requestAnimationFrame(frame);
    }
    function stopLoop() { if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; } }

    function playSample(i) {
      if (broken[i]) return;
      ensureCtx();                                     // inside the gesture
      /* Orphan anything already in flight BEFORE branching. The cached branch
         below returns early, so without this a slow load for a different sample
         would still pass its own token check, overwrite the shared buffers and
         report its failure over whatever is now audibly playing. */
      token++;
      loading = false;
      selected = i;
      if (loadedFor === i && buffers) { startSources(); return; }
      stopSources();
      // the shape on screen belongs to the old sample; swap to this one's
      waveform = null; restShape();
      paint();
      load(i).then(function (b) { if (b && selected === i) startSources(); });
    }

    cells.forEach(function (cell, i) {
      cell.addEventListener('click', function () {
        if (broken[i]) return;
        if (i === selected && playing) {               // press the playing one to stop
          stopSources(); paint();
          say('Stopped.');
          return;
        }
        playSample(i);
      });
    });

    toggle.addEventListener('click', function () {
      on = !on;
      if (playing && ctx && nodes) applyGains(ctx.currentTime, true);
      // the picture changes with the sound; reduced motion jumps instead of easing
      if (reducedMotion() || !playing) {
        // nothing to stay in sync with, and no loop running to advance a
        // crossfade, so jump straight to the target shape
        shapeMix = on ? 1 : 0; shapeAt = 0;
      } else {
        shapeFrom = shapeMix; shapeAt = (window.performance || Date).now();
      }
      if (!playing) drawWave();
      paint();
      say(playing
        ? 'Plugin ' + (on ? 'on' : 'off') + '. Playing ' + labelOf(selected) + '.'
        : 'Plugin ' + (on ? 'on' : 'off') + '. Press play on a sample to hear it.');
    });

    /* ---- release on scroll-out: audio should not follow you down the page ---- */
    function release() {
      token++;                                          // orphan any in-flight load
      stopSources();
      buffers = null; liveWave = null; loadedFor = -1; loading = false;
      waveform = null;                 // the live envelopes go with the buffers
      stopLoop();
      restShape();                     // back to the stored shape, or to nothing
      paint();
      say('');                       // do not leave "Playing …" in the live region
      if (ctx && ctx.state === 'running' && ctx.suspend) ctx.suspend();
    }

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { if (!e.isIntersecting) release(); });
      }, { rootMargin: '0px' });
      io.observe(root);
    }
    var onVis = function () {
      if (document.hidden) { release(); return; }
      // becoming visible: a tab opened in the background has never laid out
      if (!playing) restShape();
    };
    document.addEventListener('visibilitychange', onVis);

    var ro = null;
    if (typeof ResizeObserver === 'function' && canvas) {
      ro = new ResizeObserver(syncCanvas);
      ro.observe(canvas);
    }

    var rz = null;
    var onResize = function () {
      if (rz) cancelAnimationFrame(rz);
      rz = requestAnimationFrame(function () { if (waveform) { sizeCanvas(); drawWave(); } });
    };
    window.addEventListener('resize', onResize);

    root.__stAbDestroy = function () {
      release();
      if (io) io.disconnect();
      if (ro) ro.disconnect();
      stopLoop();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('resize', onResize);
      if (ctx && ctx.close) { try { ctx.close(); } catch (e) {} }
      ctx = null;
    };

    paint();
    say('');
    restShape();     // draws the BEFORE shape immediately; fetches no audio
  }

  function init() {
    var roots = document.querySelectorAll('[data-st-ab-root]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', function () { init(); });
  document.addEventListener('shopify:section:unload', function (e) {
    var scope = (e && e.target && e.target.querySelectorAll) ? e.target : document;
    var roots = scope.querySelectorAll('[data-st-ab-root]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__stAbDestroy) roots[i].__stAbDestroy();
    }
  });
})();
